import React from "react"
import { Box, IconButton, Stack, Typography } from "@mui/material"
import { Event } from "@/common/types"
import dayjs from "dayjs"
import LargeEventDialog from "@/dialogs/events/LargeEventDialog"
import EventSeoMeta from "@/components/eventDetails/EventSeoMeta"
import useDialogState from "@/hooks/useDialogState"
import shareIcon from "@/components/mapBox/images/copy_icon.png"
import useFavoriteEvent from "@/hooks/api/events/useFavoriteEvent"
import { useLocation, useNavigate } from "react-router-dom"
import { useSnackbar } from "@/contexts/SnackbarContext"
import useIsMobile from "@/hooks/useIsMobile"
import Logo from "@/common/images/843logo_white.png"
import { useGetMe } from "@/hooks/useGetMe"
import LoginDialog from "@/dialogs/events/LoginDialog"
import { HeartFilledIcon } from "@/common/images/HeartFilledIcon"
import { HeartIcon } from "@/common/images/HeartIcon"

interface EventDetailsProps {
  events: Event[]
  selectedDate: number
  selectedCalendarDate?: Date
}

const EventDetails: React.FC<EventDetailsProps> = ({
  events,
  selectedCalendarDate,
}) => {
  const isMobile = useIsMobile()
  const { data: me } = useGetMe()
  const {
    openDialog: openLoginDialog,
    closeDialog: closeLoginDialog,
    isOpen: loginIsOpen,
  } = useDialogState({})
  const { showSnackbar } = useSnackbar()
  const location = useLocation()
  const navigate = useNavigate()

  // read straight from the URL every render instead of copying it into
  // separate state - nothing to fall out of sync when the URL changes
  const activeSearchParams = new URLSearchParams(location.search)
  const openDialogId = activeSearchParams.get("details")
    ? Number(activeSearchParams.get("eventId"))
    : null

  const closeEventDialog = () => {
    navigate(location.pathname, { unstable_viewTransition: true })
  }

  const { mutateAsync: favoriteEvent } = useFavoriteEvent()

  const handleFavoriteEvent = (eventId: number) => {
    favoriteEvent(Number(eventId)).then((res) => {
      if (res.message === "Event favorited") {
        showSnackbar("Event Favorited", "success")
      } else {
        showSnackbar("Event removed from favorites", "error")
      }
    })
  }

  // same URL shape sitemap.ts generates for this event - keep click-through,
  // share links, and the sitemap all pointing at one canonical URL per event
  const buildEventDeepLink = (eventId: number) => {
    const dateParam = selectedCalendarDate
      ? `&date=${selectedCalendarDate?.getTime()}`
      : ""
    return `${location.pathname}?eventId=${eventId}&details=true${dateParam}`
  }

  const copyToClipBoard = async (eventId: number) => {
    try {
      const shareUrl = `${window.location.origin}${buildEventDeepLink(eventId)}`
      await navigator.clipboard.writeText(shareUrl)
      showSnackbar("Copied link to event!", "success")
    } catch (err) {
      console.error("could not copy", err)
    }
  }

  const likeAndShareButtonStyling = {
    backgroundColor: "white",
    width: "30px",
    height: "30px",
    padding: ".5rem",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: ".2rem",
    marginRight: ".2rem",
  }

  return (
    <Box
      sx={{
        display: "grid",
        cursor: "pointer",
        gap: isMobile ? "1rem" : "3rem",
        gridTemplateColumns: {
          xs: "minmax(280px, 400px)",
          sm: "minmax(280px, 400px)",
          md: "repeat(2, minmax(280px, 400px))",
          lg: "repeat(2, minmax(280px, 400px))",
        },
        justifyContent: "center",
        width: "100%",
        paddingBottom: "1rem",
        paddingX: { lg: "4rem", md: "2rem", sm: "1rem", xs: "1rem" },
      }}
    >
      {openDialogId && <EventSeoMeta eventId={openDialogId} />}
      {events.map((event) => (
        <div key={event.event_id}>
          {isMobile ? (
            <Box
              onClick={() => navigate(buildEventDeepLink(event.event_id), { unstable_viewTransition: true })}
              sx={{
                display: "flex",
                width: "100%",
                position: "relative",
                backgroundColor: "#2C2C2C",
                borderRadius: "8px",
              }}
            >
              <img
                src={event.event_images ? event.event_images[0].image_url : ""}
                alt={event.type_name}
                style={{
                  width: "100px",
                  borderBottomLeftRadius: "8px",
                  borderTopLeftRadius: "8px",
                  paddingRight: "4px",
                  objectFit: "cover",
                }}
              />
              <Stack
                alignContent="center"
                justifyContent={"space-between"}
                gap={2}
                sx={{ width: "100%", height: "80%" }}
              >
                <Stack>
                  <Typography variant="h2" color="white">
                    {event.title.length > 20
                      ? `${event.title.slice(0, 20)}...`
                      : event.title}
                  </Typography>
                  <Typography variant="body1" color="white">
                    {event.location_title}
                  </Typography>
                </Stack>
                <Stack gap={0.5} paddingBottom={0.5}>
                  <Typography
                    variant="body1"
                    color="white"
                    fontWeight={400}
                    fontSize={15}
                  >
                    {dayjs(event.event_start_timestamp).format("hh:mm A")} -{" "}
                    <span style={{ fontStyle: "italic" }}>{event.price}</span>
                  </Typography>
                  <Typography
                    variant="body1"
                    color="white"
                    fontWeight={300}
                    fontSize={15}
                  >
                    {event.address.split(",")[1]}, {event.address.split(",")[2]}
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          ) : (
            <Box
              key={event.event_id}
              onClick={() => navigate(buildEventDeepLink(event.event_id), { unstable_viewTransition: true })}
              sx={{
                position: "relative",
                overflow: "hidden",
                borderRadius: "8px",
                width: "100%",
                maxHeight: "280px",
                minHeight: "90px",
              }}
            >
              <Stack
                direction="row"
                gap={1}
                style={{
                  position: "absolute",
                  right: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "right",
                  width: "100%",
                  padding: ".3rem",
                  margin: ".3rem",
                  zIndex: "+5",
                }}
              >
                <IconButton
                  style={likeAndShareButtonStyling}
                  onClick={(e) => {
                    e.stopPropagation()
                    copyToClipBoard(event.event_id)
                  }}
                >
                  <img
                    style={{ width: "17px", height: "17px" }}
                    src={shareIcon}
                    alt="share event"
                  />
                </IconButton>
                <IconButton
                  style={likeAndShareButtonStyling}
                  onClick={(e) => {
                    e.stopPropagation()
                    if (me.length) {
                      handleFavoriteEvent(event.event_id)
                    } else {
                      openLoginDialog({})
                    }
                  }}
                >
                  {event?.favorited ? (
                    <HeartFilledIcon />
                  ) : (
                    <HeartIcon map={true} />
                  )}
                </IconButton>
              </Stack>
              <img
                src={
                  event?.event_images ? event.event_images[0].image_url : Logo
                }
                alt={event.location_title}
                style={{
                  width: "100%",
                  height: "250px",
                  borderRadius: "8px",
                  display: "block",
                  objectFit: "cover",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  bottom: -20,
                  paddingTop: "2rem",
                  width: "100%",
                  backgroundImage:
                    "linear-gradient(0deg, rgba(2,0,36,1) 0%, rgba(0,0,0,1) 70%, rgba(0,0,0,0) 90%)",
                }}
              >
                <Stack sx={{ display: "flex", margin: "1rem" }}>
                  {/* event icon here */}
                  <Box display="flex">
                    {event.event_types && (
                      <img
                        // src={iconKey[event.event_types[0].machine_name]}
                        src={event.event_types[0].icon_link}
                        alt={event.type_name}
                        style={{
                          width: "18px",
                          height: "18px",
                          filter: "brightness(0) invert(1)",
                          paddingRight: "4px",
                        }}
                      />
                    )}

                    <Typography variant="h3" color="white">
                      {event.title}
                    </Typography>
                  </Box>
                  <style>
                    {`
                  .hide-scrollbar {
                    scrollbar-width: none; /* For Firefox */
                  }
                  .hide-scrollbar::-webkit-scrollbar {
                    display: none; /* For Chrome, Safari, and Opera */
                  }
                `}
                  </style>
                  <Box
                    className="hide-scrollbar"
                    sx={{
                      overflowX: "scroll",
                      height: "32px",
                      width: "100%",
                    }}
                  >
                    <Typography variant="h3" color="white" fontWeight={400}>
                      {event.location_title},{" "}
                      {dayjs(event.event_start_timestamp).format("MMM, D - hA")}
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          )}
          {event.event_id === openDialogId && (
            <LargeEventDialog
              isOpen={true}
              closeDialog={closeEventDialog}
              eventId={event.event_id}
              calendarDate={String(selectedCalendarDate?.getTime())}
            />
          )}
          <LoginDialog
            closeDialog={closeLoginDialog}
            isOpen={loginIsOpen}
            message="Log in to favorite events!"
          />
        </div>
      ))}
    </Box>
  )
}

export default EventDetails
